import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { LayoutDashboard, ClipboardList, ListChecks, Settings, User, LogOut } from 'lucide-react';
import { useActiveAudit } from '../contexts/ActiveAuditContext';
import { companyService } from '../services';
import { useIsMobile } from '../hooks/useMediaQuery';
import BottomTabBar from './BottomTabBar';
import { STAGE_BANNER_HEIGHT } from './StageBanner';
import { Logo } from './ds';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const NAV_ITEMS = [
  {
    key: 'dashboard', path: '/dashboard', label: 'Übersicht', icon: LayoutDashboard,
    isActive: (p) => p === '/dashboard',
  },
  {
    key: 'audits', path: '/audits', label: 'Audits', icon: ClipboardList,
    isActive: (p) => p.startsWith('/audits') && !p.includes('/controls'),
  },
  {
    key: 'controls', label: 'Kontrollen', icon: ListChecks,
    isActive: (p) => p.includes('/controls'),
    resolvePath: (activeAuditId) => activeAuditId ? `/audits/${activeAuditId}/controls` : '/audits',
  },
  {
    key: 'settings', path: '/settings', label: 'Einstellungen', icon: Settings,
    isActive: (p) => p.startsWith('/settings'),
  },
  {
    key: 'account', path: '/account', label: 'Konto', icon: User,
    isActive: (p) => p.startsWith('/account'),
  },
];

const Layout = ({ children }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useUser();
  const { signOut } = useClerk();
  const { activeAuditId } = useActiveAudit();
  const isMobile = useIsMobile();

  const [role, setRole] = useState('');
  useEffect(() => {
    companyService.getMyCompany()
      .then(res => setRole(res.data.ownerPosition || ''))
      .catch(() => {});
  }, []);

  const fn          = user?.firstName || '';
  const ln          = user?.lastName  || '';
  const email       = user?.primaryEmailAddress?.emailAddress || '';
  const displayName = (fn || ln) ? `${fn} ${ln}`.trim() : email.split('@')[0] || 'User';
  const initials    = [(fn || email)[0], ln?.[0]].filter(Boolean).join('').toUpperCase() || 'U';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: FONT }}>

      {/* ── Sidebar (hidden below --bp-split, replaced by BottomTabBar) ── */}
      {!isMobile && <aside style={{
        width: 240, flexShrink: 0,
        background: '#FFFFFF',
        borderRight: '1px solid #D7DCE4',
        display: 'flex', flexDirection: 'column',
        // top-Offset: unter dem fixen StageBanner beginnen (0 ohne Banner)
        position: 'fixed', top: STAGE_BANNER_HEIGHT, left: 0, bottom: 0,
        zIndex: 100,
      }}>

        {/* Brand */}
        <div style={{
          padding: '20px 20px 18px', display: 'flex', alignItems: 'center',
          borderBottom: '1px solid #D7DCE4',
        }}>
          <Logo variant="lockup" height={32} />
        </div>

        {/* Nav */}
        <nav style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {NAV_ITEMS.map(item => {
            const active = item.isActive(location.pathname);
            const Icon = item.icon;
            const target = item.resolvePath ? item.resolvePath(activeAuditId) : item.path;
            return (
              <button
                key={item.key}
                onClick={() => navigate(target)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '9px 11px',
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  textAlign: 'left', fontFamily: FONT,
                  fontSize: 14, fontWeight: active ? 600 : 500,
                  background: active ? '#F2F6FE' : 'transparent',
                  color: active ? '#1B4098' : '#2C3646',
                  transition: 'background 120ms, color 120ms',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#F5F7FA'; }}}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; }}}
              >
                <Icon width={18} height={18} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {process.env.REACT_APP_VERSION && (
          <div style={{ padding: '8px 12px 0', fontSize: 10.5, color: '#B6BDC9', textAlign: 'left' }}>
            v{process.env.REACT_APP_VERSION}
          </div>
        )}

        {/* User */}
        <div style={{ padding: 12, borderTop: '1px solid #D7DCE4', display: 'flex', alignItems: 'center', gap: 10 }}>
          {user?.hasImage ? (
            <img
              src={user.imageUrl}
              alt={displayName}
              style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
            />
          ) : (
            <span style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: '#D7DCE4', color: '#2C3646',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 600,
            }}>{initials}</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 600, color: '#101623',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{displayName}</div>
            <div style={{ fontSize: 11, color: '#667085' }}>{role || 'Inhaber'}</div>
          </div>
          <button
            onClick={() => signOut()}
            title="Abmelden"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#8A93A3', padding: 6, borderRadius: 6, flexShrink: 0,
              display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#2C3646'; e.currentTarget.style.background = '#F5F7FA'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#8A93A3'; e.currentTarget.style.background = 'none'; }}
          >
            <LogOut width={16} height={16} />
          </button>
        </div>
      </aside>}

      {/* ── Main ────────────────────────────────────────────── */}
      <div style={{
        marginLeft: isMobile ? 0 : 240, flex: 1, background: '#F5F7FA',
        minHeight: 'var(--vh-fill)',
        paddingBottom: isMobile ? 'calc(56px + var(--safe-bottom))' : 0,
      }}>
        {children}
      </div>

      {isMobile && (
        <BottomTabBar
          items={NAV_ITEMS}
          activeAuditId={activeAuditId}
          pathname={location.pathname}
          onNavigate={navigate}
          onSignOut={signOut}
          displayName={displayName}
          role={role}
        />
      )}
    </div>
  );
};

export default Layout;
